import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGPieChart } from '../../components/primitives/PGChart';
import {
  opportunitiesByBranch,
  PIPELINE_BRANCH,
  PIPELINE_GROUP,
  repsByBranch,
  renewalsByBranch,
  OPEN_STAGE_IDS,
  fmtR,
  fmtRk,
} from '../../utils/opportunityData';
import { getBranch } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const {
  FiTrendingUp, FiZap, FiLayers, FiDollarSign, FiAward, FiAlertTriangle, FiRefreshCw,
} = FiIcons;

const COLS = [
  { key: 'id',             label: 'Deal',    align: 'left'  },
  { key: 'customerSegmentName', label: 'Segment', align: 'left' },
  { key: 'stageName',      label: 'Stage',   align: 'left'  },
  { key: 'value',          label: 'Value_R', align: 'right' },
  { key: 'probability',    label: 'Prob',    align: 'right' },
  { key: 'daysInStage',    label: 'Days',    align: 'right' },
  { key: 'repName',        label: 'Rep',     align: 'left'  },
  { key: 'expectedCloseDate', label: 'ETA',  align: 'right' },
];

const renewalTone = (s) => ({
  overdue: 'alert', urgent: 'alert', upcoming: 'warn', scheduled: 'info',
}[s] || 'neutral');

export default function OpportunitiesBranchView({ branchId }) {
  const branch = getBranch(branchId);
  const pipeline = PIPELINE_BRANCH[branchId];
  const opps = opportunitiesByBranch(branchId);
  const reps = repsByBranch(branchId);
  const renewals = renewalsByBranch(branchId);

  const [selectedId, setSelectedId] = useState(() => {
    const open = opps
      .filter((o) => OPEN_STAGE_IDS.includes(o.stageId) && o.outcome !== 'lost')
      .sort((a, b) => b.value - a.value);
    return open[0]?.id;
  });
  const [sortKey, setSortKey] = useState('value');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!opps.length) return [];
    const copy = [...opps];
    copy.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? ''); const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [opps, sortKey, sortDir]);

  if (!branch || !pipeline) {
    return (
      <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-[10px] uppercase tracking-[0.3em]">
        Pipeline_Not_Found
      </div>
    );
  }

  const selected = opps.find((o) => o.id === selectedId);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const renderSortIndicator = (key) => {
    if (key !== sortKey) return null;
    return <span className="text-[#D71921] ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const healthy = pipeline.stalledCount === 0 && pipeline.winRate >= PIPELINE_GROUP.winRate;
  const watch = pipeline.stalledCount > 0 && pipeline.stalledCount <= 3;
  const healthLabel = healthy ? 'Healthy' : watch ? 'Watch' : 'Attention';
  const healthTone = healthy ? 'ok' : watch ? 'warn' : 'alert';

  const avgDealAccent = pipeline.avgDealSize >= PIPELINE_GROUP.avgDealSize ? PG.emerald : PG.amber;
  const winRateAccent = pipeline.winRate >= PIPELINE_GROUP.winRate ? PG.emerald : PG.amber;
  const stalledAccent = pipeline.stalledCount > 0 ? PG.red : PG.emerald;

  const renewals90d = renewals.filter((r) => r.daysToExpiry <= 90).length;
  const renewalsAccent = renewals90d > 0 ? PG.amber : PG.emerald;

  const segmentPie = pipeline.segmentBreakdown.map((s) => ({ name: s.name, value: s.count }));

  const sortedReps = [...reps].sort((a, b) => b.revenueClosedMTD - a.revenueClosedMTD);
  const sortedRenewals = [...renewals].sort((a, b) => a.daysToExpiry - b.daysToExpiry);

  const stageTone = (tone) => tone || 'neutral';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-3 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-neutral-500">
            SALES / OPPORTUNITIES / BRANCH
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-2xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
              {branch.town} &middot; {branch.country} &middot; {pipeline.openCount} open &middot; {pipeline.bigTicketCount} big-ticket
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone="info">Peer: {branch.peerGroup}</StatusPill>
          <StatusPill tone={healthTone}>Pipeline_Health: {healthLabel}</StatusPill>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-3 flex-shrink-0">
        <KPICard
          title="Pipeline_Value"
          value={fmtRk(pipeline.pipelineValue)}
          target={`${fmtRk(pipeline.weightedPipeline)} weighted`}
          icon={FiTrendingUp}
          accent={PG.cyan}
        />
        <KPICard
          title="Active_Deals"
          value={pipeline.openCount}
          unit="deals"
          target="-"
          icon={FiZap}
          accent={PG.white}
        />
        <KPICard
          title="Avg_Deal_Size"
          value={fmtRk(pipeline.avgDealSize)}
          target={`${fmtRk(PIPELINE_GROUP.avgDealSize)} group`}
          trend={pipeline.avgDealSize >= PIPELINE_GROUP.avgDealSize ? 'up' : 'down'}
          trendValue={pipeline.avgDealSize >= PIPELINE_GROUP.avgDealSize ? 'Above' : 'Below'}
          icon={FiLayers}
          accent={avgDealAccent}
        />
        <KPICard
          title="Win_Rate"
          value={`${pipeline.winRate}%`}
          target={`${PIPELINE_GROUP.winRate}% group`}
          trend={pipeline.winRate >= PIPELINE_GROUP.winRate ? 'up' : 'down'}
          trendValue={`${(pipeline.winRate - PIPELINE_GROUP.winRate).toFixed(0)}%`}
          icon={FiAward}
          accent={winRateAccent}
        />
        <KPICard
          title="Forecast_30d"
          value={fmtRk(pipeline.forecast30d)}
          target="weighted"
          icon={FiDollarSign}
          accent={PG.amber}
        />
        <KPICard
          title="Stalled"
          value={pipeline.stalledCount}
          unit="deals"
          target="0"
          icon={FiAlertTriangle}
          accent={stalledAccent}
        />
        <KPICard
          title="Renewals_90d"
          value={renewals90d}
          unit="due"
          target={`${renewals.length} total`}
          icon={FiRefreshCw}
          accent={renewalsAccent}
        />
      </div>

      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        <Panel title="Pipeline_Funnel" subtitle="open deals by stage" className="col-span-12 flex-shrink-0 h-28">
          <div className="h-full grid grid-cols-5 gap-0">
            {pipeline.stageBreakdown.map((s) => {
              const toneColor = {
                ok: PG.emerald, warn: PG.amber, info: PG.cyan, alert: PG.red, neutral: PG.white,
              }[s.tone] || PG.white;
              return (
                <div key={s.id} className="flex flex-col justify-center items-center border-r border-white/5 last:border-r-0 px-2">
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">{s.name}</div>
                  <div className="font-doto text-2xl leading-none mt-1" style={{ color: toneColor }}>{s.count}</div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-400 mt-1">{fmtRk(s.value)}</div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel
          title="Opportunity_Roster"
          subtitle="click row to inspect // click header to sort"
          right={<StatusPill tone="info">{opps.length} total</StatusPill>}
          className="col-span-7 flex-1 min-h-0"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                <tr className="border-b border-white/10">
                  {COLS.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className={`px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 cursor-pointer hover:text-white select-none ${c.align === 'right' ? 'text-right' : ''}`}
                    >
                      {c.label}{renderSortIndicator(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((o) => {
                  const isSel = o.id === selected?.id;
                  const stalledBg = o.isStalled && !isSel ? 'bg-[#D71921]/5' : '';
                  return (
                    <tr
                      key={o.id}
                      onClick={() => setSelectedId(o.id)}
                      className={`border-b border-white/5 transition-colors cursor-pointer ${isSel ? 'bg-[#D71921]/10 border-l-2 border-l-[#D71921]' : `${stalledBg} hover:bg-white/5`}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white truncate max-w-[180px]">{o.customer}</div>
                        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">{o.id}</div>
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-neutral-300">
                        {o.customerSegmentName}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill tone={stageTone(o.stageTone)}>{o.stageName}</StatusPill>
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-white">
                        {fmtRk(o.value)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[10px] text-neutral-300">
                        {Math.round(o.probability * 100)}%
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-doto text-base" style={{ color: o.daysInStage > 18 ? PG.red : o.daysInStage > 10 ? PG.amber : PG.white }}>
                          {o.daysInStage}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400 truncate max-w-[120px]">{o.repName}</td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[10px] text-neutral-300">{o.expectedCloseDate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="col-span-5 flex flex-col gap-3 min-h-0">
          <Panel title="Selected_Opportunity" className="flex-shrink-0">
            {selected ? (
              <div className="p-3">
                <div className="flex items-baseline justify-between mb-2">
                  <div>
                    <div className="font-doto text-2xl text-white leading-none">{selected.id}</div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                      {selected.customerSegmentName}
                    </div>
                  </div>
                  <StatusPill tone={stageTone(selected.stageTone)}>{selected.stageName}</StatusPill>
                </div>
                <div className="text-white text-sm">{selected.customer}</div>
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 mt-0.5">
                  Rep: {selected.repName}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Value</div>
                    <div className="font-doto text-base mt-1" style={{ color: PG.red }}>{fmtR(selected.value)}</div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Weighted</div>
                    <div className="font-doto text-base mt-1" style={{ color: PG.violet }}>{fmtR(selected.weightedValue)}</div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Probability</div>
                    <div className="font-doto text-base text-white mt-1">{Math.round(selected.probability * 100)}%</div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Days_In_Stage</div>
                    <div className="font-doto text-base mt-1" style={{ color: selected.daysInStage > 18 ? PG.red : PG.white }}>
                      {selected.daysInStage}
                    </div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Expected_Close</div>
                    <div className="font-doto text-base text-white mt-1">
                      {selected.expectedCloseDate}
                      <span className="font-mono-tech text-[9px] text-neutral-500 ml-1">
                        ({selected.expectedCloseDaysAhead >= 0 ? '+' : ''}{selected.expectedCloseDaysAhead}d)
                      </span>
                    </div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Source</div>
                    <div className="font-mono-tech text-[11px] text-neutral-300 uppercase tracking-wider mt-1">{selected.sourceName}</div>
                  </div>
                </div>

                {selected.productMix?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {selected.productMix.map((p, i) => (
                      <StatusPill key={i} tone="neutral">{p}</StatusPill>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 flex items-center justify-center font-mono-tech text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                NO_DEAL_SELECTED
              </div>
            )}
          </Panel>

          <Panel title="Segment_Breakdown" subtitle="deals by customer segment" className="flex-1 min-h-0">
            <div className="h-full p-2">
              {segmentPie.length === 0 ? (
                <div className="h-full flex items-center justify-center font-mono-tech text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                  NO_SEGMENT_DATA
                </div>
              ) : (
                <PGPieChart data={segmentPie} />
              )}
            </div>
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 flex-shrink-0 h-44">
        <Panel
          title="Rep_Mini_Leaderboard"
          subtitle={`${sortedReps.length} reps // by revenue closed`}
          className="col-span-5"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            {sortedReps.length === 0 ? (
              <div className="h-full flex items-center justify-center font-mono-tech text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                NO_REPS_ASSIGNED
              </div>
            ) : (
              <div>
                {sortedReps.map((r, i) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between px-3 py-2 border-b border-white/5"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="font-doto text-base text-neutral-500 w-5">{i + 1}</div>
                      <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate">{r.name}</div>
                    </div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-wider text-neutral-400 flex-shrink-0">
                      {r.wonMTD}W &middot; {fmtRk(r.revenueClosedMTD)} &middot; {r.winRate90d}%
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Renewals_Due"
          subtitle={`${sortedRenewals.length} contracts // by days to expiry`}
          className="col-span-7"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            {sortedRenewals.length === 0 ? (
              <div className="h-full flex items-center justify-center font-mono-tech text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                NO_RENEWALS_SCHEDULED
              </div>
            ) : (
              <div>
                {sortedRenewals.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between px-3 py-2 border-b border-white/5"
                  >
                    <div className="min-w-0">
                      <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate">{r.customer}</div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                        {r.customerSegmentName} &middot; {r.ownerRep}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="font-mono-tech text-[10px] uppercase tracking-wider text-neutral-400">
                        {fmtRk(r.currentValue)} &rarr; {fmtRk(r.renewalValue)}
                      </div>
                      <StatusPill tone={renewalTone(r.status)}>
                        {r.daysToExpiry < 0 ? `${Math.abs(r.daysToExpiry)}d over` : `${r.daysToExpiry}d`}
                      </StatusPill>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
